import React from "react";
import { Button, Modal, Table, Form } from "react-bootstrap";
import MilkDeliveryApi from "../../api/MilkDeliveryApi";
import AuthorizedPage from "../../component/AuthorizedPage";

interface MilkDelivery {
  id?: number;
  milkVehicleId?: number;
  milkVehicleDriverId?: number;
  deliveredGoatMilkVolume?: number;
  deliveredSheepMilkVolume?: number;
  deliveredCowMilkVolume?: number;
  deliveryDateTime?: string;
}

interface Vehicle {
  id: number;
  licencePlateCode: string;
}

interface Driver {
  id: number;
  name: string;
  surname: string;
}

interface State {
  entries: MilkDelivery[];
  vehicles: Vehicle[];
  drivers: Driver[];
  showModal: boolean;
  editingEntry: MilkDelivery | null;
  loading: boolean;
}

const defaultEntry: MilkDelivery = {
  deliveredGoatMilkVolume: 0,
  deliveredSheepMilkVolume: 0,
  deliveredCowMilkVolume: 0,
  deliveryDateTime: new Date().toISOString().slice(0, 16),
};

export default class MilkDeliveryEntry extends React.Component<{}, State> {
  state: State = {
    entries: [],
    vehicles: [],
    drivers: [],
    showModal: false,
    editingEntry: null,
    loading: false,
  };

  componentDidMount(): void {
    this.loadInitialData();
  }

  async loadInitialData() {
    this.setState({ loading: true });
    try {
      const [deliveries, vehicles, drivers] = await Promise.all([
        MilkDeliveryApi.listDeliveries(),
        MilkDeliveryApi.listVehicles(),
        MilkDeliveryApi.listDrivers(),
      ]);

      this.setState({
        entries: deliveries?.data || [],
        vehicles: vehicles?.data || [],
        drivers: drivers?.data || [],
      });
    } catch (err) {
      console.error("Veriler yuklenirken hata:", err);
    } finally {
      this.setState({ loading: false });
    }
  }

  openEditModal = (entry?: MilkDelivery) => {
    this.setState({
      editingEntry: entry ? { ...entry } : { ...defaultEntry },
      showModal: true,
    });
  };

  handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      editingEntry: {
        ...prev.editingEntry!,
        [name]: name === "deliveredCowMilkVolume" ? Number(value) : value,
        [name]: name === "deliveredSheepMilkVolume" ? Number(value) : value,
        [name]: name === "deliveredGoatMilkVolume" ? Number(value) : value,
      },
    }));
  };

  handleSave = async () => {
    const { editingEntry } = this.state;
    if (!editingEntry) return;

    try {
      const result = await MilkDeliveryApi.createOrUpdateDelivery(editingEntry);
      if (result?.success) {
        await this.loadInitialData();
        this.setState({ showModal: false, editingEntry: null });
      } else {
        alert("Kaydetme sirasinda hata olustu.");
      }
    } catch (err) {
      console.error("Kayit hatasi:", err);
    }
  };

  renderDeliveryTable() {
    const { entries, vehicles, drivers } = this.state;

    const getVehicleLabel = (id?: number) =>
      vehicles.find((v) => v.id === id)?.licencePlateCode || "-";
    const getDriverLabel = (id?: number) => {
      const d = drivers.find((d) => d.id === id);
      return d ? `${d.name} ${d.surname}` : "-";
    };

    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Araç|Şoför</th>
            <th>İnek Sütü (L)</th>
            <th>Koyun Sütü (L)</th>
            <th>Keçi Sütü (L)</th>
            <th>Tarih/Saat</th>
            <th>İşlem</th>

          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
               <td style={{ maxWidth: "50px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                 {getVehicleLabel(e.milkVehicleId)}
                 <br />
                 {getDriverLabel(e.milkVehicleDriverId)}
               </td>
              <td>{e.deliveredCowMilkVolume}</td>
              <td> {e.deliveredSheepMilkVolume} </td>
              <td>{e.deliveredGoatMilkVolume}</td>
              <td>{e.deliveryDateTime?.replace("T", " ").slice(0, 16)}</td>
              <td>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => this.openEditModal(e)}
                >
                  Düzenle
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderEntryModal() {
    const { editingEntry, vehicles, drivers } = this.state;
    if (!editingEntry) return null;

    return (
      <Modal
        show={true}
        onHide={() => this.setState({ showModal: false, editingEntry: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Süt Teslimatı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Araç</Form.Label>
              <Form.Select
                name="milkVehicleId"
                value={editingEntry.milkVehicleId || ""}
                onChange={this.handleChange}
              >
                <option value="">Seçiniz</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.licencePlateCode}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Şoför</Form.Label>
              <Form.Select
                name="milkVehicleDriverId"
                value={editingEntry.milkVehicleDriverId || ""}
                onChange={this.handleChange}
              >
                <option value="">Seçiniz</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.surname}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Hacim İnek(Litre)</Form.Label>
              <Form.Control
                name="deliveredCowMilkVolume"
                type="number"
                value={editingEntry.deliveredCowMilkVolume || ""}
                onChange={this.handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
                          <Form.Label>Hacim Koyun(Litre)</Form.Label>
                          <Form.Control
                            name="deliveredSheepMilkVolume"
                            type="number"
                            value={editingEntry.deliveredSheepMilkVolume || ""}
                            onChange={this.handleChange}
                          />
                        </Form.Group>
            <Form.Group className="mb-3">
                          <Form.Label>Hacim Keçi(Litre)</Form.Label>
                          <Form.Control
                            name="deliveredGoatMilkVolume"
                            type="number"
                            value={editingEntry.deliveredGoatMilkVolume || ""}
                            onChange={this.handleChange}
                          />
                        </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Teslimat Tarih/Saati</Form.Label>
              <Form.Control
                name="deliveryDateTime"
                type="datetime-local"
                value={editingEntry.deliveryDateTime || ""}
                onChange={this.handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => this.setState({ showModal: false, editingEntry: null })}
          >
            İptal
          </Button>
          <Button variant="primary" onClick={this.handleSave}>
            Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    return (
      <AuthorizedPage role="ORDER_MANAGER" isLoading={this.state.loading}>
        <div className="container mt-4">
          <h4>Süt Teslimatı Girişi</h4>
          <Button className="mb-3" onClick={() => this.openEditModal()}>
            + Yeni Teslimat
          </Button>
          {this.renderDeliveryTable()}
          {this.renderEntryModal()}
        </div>
      </AuthorizedPage>
    );
  }
}
